import React from "react";
import { Link } from "react-router-dom";
import PageLayout from "@/components/feature/PageLayout";
import SectionTitle from "@/components/base/SectionTitle";
import { useHashScroll } from "@/hooks/useHashScroll";
import { envTestPriceList } from "@/mocks/pricelist";

export default function EnvTestPrice() {
  useHashScroll();
  return (
    <PageLayout>
      {/* Hero */}
      <div className="relative py-14 md:py-20 bg-accent-700 overflow-hidden">
        <div className="absolute inset-0 opacity-15">
          <img
            src="https://readdy.ai/api/search-image?query=Modern%20environmental%20testing%20laboratory%20with%20temperature%20humidity%20chambers%2C%20clean%20white%20interior%2C%20professional%20industrial%20atmosphere%2C%20soft%20lighting%2C%20minimal%20composition&width=1600&height=600&seq=envtest-hero&orientation=landscape"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/20" />
        <div className="relative z-10 w-full px-4 md:px-8">
          <div className="max-w-4xl mx-auto">
            <nav className="mb-4">
              <ol className="flex items-center gap-2 text-sm text-background-300">
                <li>
                  <Link to="/" className="hover:text-background-100 transition-colors">
                    ホーム
                  </Link>
                </li>
                <li>
                  <i className="ri-arrow-right-s-line text-xs" />
                </li>
                <li>
                  <Link to="/service" className="hover:text-background-100 transition-colors">
                    サービス
                  </Link>
                </li>
                <li>
                  <i className="ri-arrow-right-s-line text-xs" />
                </li>
                <li>
                  <Link to="/service/test-guide" className="hover:text-background-100 transition-colors">
                    受託試験のご案内
                  </Link>
                </li>
                <li>
                  <i className="ri-arrow-right-s-line text-xs" />
                </li>
                <li className="text-background-100">その他環境試験 料金表</li>
              </ol>
            </nav>
            <h1 className="text-3xl md:text-4xl font-black text-background-50">
              その他環境試験 料金表
            </h1>
            <p className="text-foreground-300 text-sm mt-2">Environmental Test Price List</p>
          </div>
        </div>
      </div>

      <div className="w-full px-4 md:px-8 py-12 md:py-16">
        <div className="max-w-5xl mx-auto">
          {/* Price Table */}
          <SectionTitle
            title={envTestPriceList.title}
            subtitle="高温放置・耐湿度・振動・塩水噴霧など各種環境試験の料金一覧"
            centered
          />

          <div className="mt-8">
            <p className="text-right text-xs text-foreground-500 mb-2">{envTestPriceList.unitLabel}</p>
            {envTestPriceList.groups.map((group, gi) => (
              <div
                key={gi}
                id="env-test"
                className="mb-8 scroll-mt-24 md:scroll-mt-28"
              >
                <h3 className="text-base font-bold text-foreground-900 mb-1">{group.name}</h3>
                <div className="overflow-x-auto rounded-xl border border-background-200/70">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-accent-100 text-accent-900">
                        <th className="px-4 py-2.5 text-left font-semibold text-xs whitespace-nowrap">内容</th>
                        <th className="px-4 py-2.5 text-right font-semibold text-xs whitespace-nowrap">基本料金</th>
                        <th className="px-4 py-2.5 text-center font-semibold text-xs whitespace-nowrap">単位</th>
                        <th className="px-4 py-2.5 text-right font-semibold text-xs whitespace-nowrap">料金</th>
                        <th className="px-4 py-2.5 text-left font-semibold text-xs whitespace-nowrap">備考</th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.items.map((item, ii) => (
                        <tr
                          key={ii}
                          className={ii % 2 === 0 ? "bg-background-50" : "bg-background-100/50"}
                        >
                          <td className="px-4 py-2.5 text-foreground-800 whitespace-nowrap">{item.content}</td>
                          <td className="px-4 py-2.5 text-right text-foreground-700 font-medium whitespace-nowrap">{item.baseFee === "0" ? "—" : item.baseFee}</td>
                          <td className="px-4 py-2.5 text-center text-foreground-600 whitespace-nowrap">{item.unit}</td>
                          <td className="px-4 py-2.5 text-right text-foreground-700 font-medium whitespace-nowrap">{item.fee === "-" ? "—" : item.fee}</td>
                          <td className="px-4 py-2.5 text-foreground-600 text-xs min-w-[180px]">{item.note}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="mt-4 flex justify-end">
                  <Link
                    to="/contact"
                    className="inline-flex items-center gap-1.5 text-sm text-accent-600 hover:text-accent-700 font-semibold transition-colors whitespace-nowrap"
                  >
                    <i className="ri-mail-send-line" />
                    お見積り・ご相談はこちら
                  </Link>
                </div>
              </div>
            ))}
            <div className="mt-4 space-y-1 text-xs text-foreground-500">
              {envTestPriceList.footnotes.map((note, ni) => (
                <p key={ni}>{note}</p>
              ))}
            </div>
          </div>

          {/* Usage Rules */}
          <div className="mt-12 bg-background-100 rounded-xl p-6 md:p-8 border border-background-200/70">
            <h3 className="text-base font-bold text-foreground-900 mb-1">受託試験のご利用にあたって</h3>
            <p className="text-xs text-foreground-500 mb-5">料金算出方法・割引・キャンセルポリシーなど</p>

            <ol className="space-y-4">
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-accent-100 text-accent-700 text-xs font-bold mt-0.5">1</span>
                <p className="text-sm text-foreground-700 leading-relaxed">
                  受託試験料金は次の様に算出します。<br />
                  <span className="text-foreground-900 font-medium">受託試験料金＝基本料金＋単位（時間・種類）×料金</span><br />
                  基本料金には試験報告書と返送料が含まれます。
                  （返送料2個目から実費発生いたします。）
                </p>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-accent-100 text-accent-700 text-xs font-bold mt-0.5">2</span>
                <p className="text-sm text-foreground-700 leading-relaxed">
                  長時間試験実施の場合は、使用時間料金の割引に応じますので、ご相談ください。
                </p>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-accent-100 text-accent-700 text-xs font-bold mt-0.5">3</span>
                <p className="text-sm text-foreground-700 leading-relaxed">
                  依頼試験のキャンセルまたは日程変更につきましては、速やかにご連絡くださいますようお願い申し上げます。なお、試験準備期間に入ってからのキャンセル等につきましては、所定のキャンセル料を申し受けます。あらかじめご了承賜りますようお願い申し上げます。<br />
                  現在のキャンセル料について&rarr;15営業日前 10％、10営業日前 30％、5営業日前 50％、2営業日前 100％
                </p>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-accent-100 text-accent-700 text-xs font-bold mt-0.5">4</span>
                <p className="text-sm text-foreground-700 leading-relaxed">
                  観察・測定の為試験サンプルを中間抜き取りされる場合は、中間抜き取りのみの場合2,500円～3,000円。抜き取り及び返却送付の場合は3,500円を申し受けます。
                </p>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-accent-100 text-accent-700 text-xs font-bold mt-0.5">5</span>
                <p className="text-sm text-foreground-700 leading-relaxed">
                  指定外の試験につきましては、別途費用を申し受けます。
                </p>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-accent-100 text-accent-700 text-xs font-bold mt-0.5">6</span>
                <p className="text-sm text-foreground-700 leading-relaxed">
                  試験前後等に立ち会いが必要な場合、立ち会い時間によっては手数料が発生いたします。
                </p>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-accent-100 text-accent-700 text-xs font-bold mt-0.5">7</span>
                <p className="text-sm text-foreground-700 leading-relaxed">
                  本料金表に記載のないその他の試験や分析依頼も応じますので御相談ください。
                </p>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-accent-100 text-accent-700 text-xs font-bold mt-0.5">8</span>
                <p className="text-sm text-foreground-700 leading-relaxed">
                  記載の料金には消費税を含んでおりませんので、別途申し受けいたします。
                </p>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-accent-100 text-accent-700 text-xs font-bold mt-0.5">9</span>
                <p className="text-sm text-foreground-700 leading-relaxed">
                  記載の料金は<span className="text-foreground-900 font-medium">2026年5月1日</span>現在の料金です。
                </p>
              </li>
            </ol>
          </div>

          {/* Back Link */}
          <div className="mt-10 text-center">
            <Link
              to="/service/test-guide"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-background-100 text-foreground-700 font-semibold text-sm hover:bg-background-200 transition-colors border border-background-200/70"
            >
              <i className="ri-arrow-left-line" />
              受託試験のご案内へ戻る
            </Link>
          </div>
        </div>
      </div>
    </PageLayout>
  );
}